import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { useAuth } from "./hooks/useAuth";
import { WsProvider } from "./lib/ws-provider";
import { LoginPage } from "./routes/auth/login";
import { SignedOutPage } from "./routes/auth/signed-out";
import { AppShell } from "./components/AppShell";
import { DashboardHubPage } from "./features/dashboard/pages/DashboardHubPage";
import { VerificationStation } from "./features/verification/components/VerificationStation";
import { JobTypeListPage } from "./features/admin/pages/JobTypeListPage";
import { JobTypeDetailPage } from "./features/admin/pages/JobTypeDetailPage";
import { RoleManagementPage } from "./features/admin/pages/RoleManagementPage";
import { BatchListPage } from "./features/batches/pages/BatchListPage";
import { BatchDetailPage } from "./features/batches/pages/BatchDetailPage";
import { QueueDashboardPage } from "./features/queues/pages/QueueDashboardPage";
import { OperatorPreferencesPage } from "./features/settings/pages/OperatorPreferencesPage";
import { ExportFormatListPage } from "./features/admin/pages/ExportFormatListPage";
import { ExportFormatWizardPage } from "./features/admin/pages/ExportFormatWizardPage";
import { ExportFormatDetailPage } from "./features/admin/pages/ExportFormatDetailPage";
import { ExportHistoryPage } from "./features/admin/pages/ExportHistoryPage";
import { OutputsCatalogPage } from "./features/admin/outputs/OutputsCatalogPage";
import { InputChannelsPage } from "./features/admin/pages/InputChannelsPage";
import { OpsConsolePage } from "./features/ops/pages/OpsConsolePage";
import { AnalyticsPage } from "./features/analytics/pages/AnalyticsPage";
import { OperatorDetailPage } from "./features/analytics/pages/OperatorDetailPage";
import { DocumentTrailPage } from "./features/analytics/pages/DocumentTrailPage";
import { BatchTrailPage } from "./features/analytics/pages/BatchTrailPage";
import { ReportBuilderPage } from "./features/analytics/pages/ReportBuilderPage";
import { DetachedFieldPanel } from "./features/verification/pages/DetachedFieldPanel";
import { LlmSettingsPage } from "./features/admin/pages/LlmSettingsPage";
import { LlmUsagePage } from "./features/admin/pages/LlmUsagePage";
import { FormConfigPage } from "./features/admin/form-config/FormConfigPage";
import { JobVersionWorkspace } from "./features/admin/workspace/JobVersionWorkspace";
import { BatchFixPage } from "./features/batch-fix/BatchFixPage";
import { TenantSettingsPage } from "./features/admin/settings/TenantSettingsPage";
import { FailedDocumentsPage } from "./features/admin/failed-documents/FailedDocumentsPage";
import { OcrHealthPage } from "./features/admin/ocr-health/OcrHealthPage";
import { AutomationGuidePage } from "./features/automation-guide/AutomationGuidePage";
import { FeedExhaustedPage } from "./features/queues/pages/FeedExhaustedPage";
import { TransformPluginListPage } from "./features/admin/transform-plugins/TransformPluginListPage";
import { TransformPluginDetailPage } from "./features/admin/transform-plugins/TransformPluginDetailPage";
import { NamedChainListPage } from "./features/admin/named-chains/NamedChainListPage";
import { NamedChainCreateDialog } from "./features/admin/named-chains/NamedChainCreateDialog";
import { NamedChainDetailPage } from "./features/admin/named-chains/NamedChainDetailPage";
import { LookupTableListPage } from "./features/admin/lookup-tables/LookupTableListPage";
import { LookupTableDetailPage } from "./features/admin/lookup-tables/LookupTableDetailPage";
import CountersListPage from "./features/admin/counters/pages/CountersListPage";
import { CategoryListPage } from "./features/admin/categories/CategoryListPage";
import { QueueBlocksListPage } from "./features/admin/queue-blocks/pages/QueueBlocksListPage";
import { QueueInstructionsListPage } from "./features/admin/queue-instructions/pages/QueueInstructionsListPage";
import { TeamsAdminPage } from "./features/admin/teams/pages/TeamsAdminPage";
import { AnnouncementsAdminPage } from "./features/announcements/pages/AnnouncementsAdminPage";
import { StaffMonitorPage } from "./features/admin/staff-monitor/StaffMonitorPage";
import { RetentionQueuePage } from "./features/retention/pages/RetentionQueuePage";
import { ClassifyQueuePage } from "./features/classify/pages/ClassifyQueuePage";
import { PendingReleasePage } from "./features/pending-release/PendingReleasePage";
import { HistoryPage } from "./features/history/pages/HistoryPage";
import { ConnectionsPage } from "./features/admin/connections/ConnectionsPage";
import { ConnectedAgentsPage } from "./features/admin/agents/ConnectedAgentsPage";
import { NotesInboxPage } from "./features/directed-notes/pages/NotesInboxPage";
import { DirectedNotesSentPage } from "./features/directed-notes/pages/DirectedNotesSentPage";
import { RemittanceStation } from "./features/remittance/components/RemittanceStation";
import { AutomationPanelPage } from "./features/admin/automations/panel/AutomationPanelPage";
import { CreateAutomationPage } from "./features/admin/automations/create/CreateAutomationPage";

function AuthGate({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signed-out" element={<SignedOutPage />} />
        <Route path="/panel-detached" element={<DetachedFieldPanel />} />
        {/* Verification station is full-screen, no shell */}
        {/* R-235 B2 — VS now consumes document_moved WS events (graceful-bump
            when a supervisor moves the operator's current doc). WsProvider is
            intentionally wrapped here; the prior R-202/R-203 comment that
            excluded /verify is superseded now that the VS has a WS consumer. */}
        <Route
          path="/verify/:documentId"
          element={
            <AuthGate>
              <WsProvider>
                <VerificationStation />
              </WsProvider>
            </AuthGate>
          }
        />
        {/* Remittance station (SP3) is full-screen, no shell — mirrors /verify */}
        <Route
          path="/ops/remittance/:batchId"
          element={
            <AuthGate>
              <WsProvider>
                <RemittanceStation />
              </WsProvider>
            </AuthGate>
          }
        />
        <Route
          path="/*"
          element={
            <AuthGate>
              {/* R-202 — WsProvider mounts INSIDE the auth gate (only connects
                  post-login) and ABOVE AppShell, so AppShell's useQueueCounts
                  and the QueueDashboard's useQueues read the live client.
                  Note: /verify has its own WsProvider wrap (added R-235 B2)
                  to consume document_moved events for graceful supervisor bump. */}
              <WsProvider>
                <AppShell />
              </WsProvider>
            </AuthGate>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardHubPage />} />
          <Route path="admin/job-types" element={<JobTypeListPage />} />
          <Route element={<JobVersionWorkspace />}>
            <Route path="admin/job-types/:jobTypeId" element={<JobTypeDetailPage />} />
            <Route path="admin/job-types/:jobTypeId/rules" element={<FormConfigPage />} />
            <Route path="admin/forms/:jobTypeId" element={<FormConfigPage />} />
            {/* Task 7 — A3 automation panel; E-062 — the WHERE → ON WHAT →
                IF/THEN → Review create flow (replaced the intent stepper).
                Both stay under the workspace layout route so the draft store
                + OCC autosave (useFormConfig/useDebouncedSave) are already
                mounted — neither makes its own panel-level API calls or has
                a save button. */}
            <Route path="admin/job-types/:jobTypeId/automations/new" element={<CreateAutomationPage />} />
            <Route path="admin/job-types/:jobTypeId/automations/:automationKey" element={<AutomationPanelPage />} />
          </Route>
          <Route path="admin/lookup-tables" element={<LookupTableListPage />} />
          <Route path="admin/lookup-tables/:id" element={<LookupTableDetailPage />} />
          <Route path="admin/counters" element={<CountersListPage />} />
          <Route path="admin/categories" element={<CategoryListPage />} />
          <Route path="admin/queue-blocks" element={<QueueBlocksListPage />} />
          <Route path="admin/queue-instructions" element={<QueueInstructionsListPage />} />
          {/* Task 7 — Team Hierarchy T1 builder page. Gated in AppShell nav on team.manage. */}
          <Route path="admin/teams" element={<TeamsAdminPage />} />
          <Route path="admin/announcements" element={<AnnouncementsAdminPage />} />
          {/* Task 14 (WU-2.3) — supervisor sent-notes admin page. Gated in
              AppShell nav on canSendDirectNote; the API route itself is
              server-authoritative on supervisor.direct_note / admin. */}
          <Route path="admin/directed-notes" element={<DirectedNotesSentPage />} />
          {/* R-236 — Staff Status Monitor; page-level perm gate on supervisor.ops_console */}
          <Route path="admin/operators" element={<StaffMonitorPage />} />
          <Route path="admin/roles" element={<RoleManagementPage />} />
          <Route path="queues" element={<QueueDashboardPage />} />
          <Route path="queues/feed-exhausted" element={<FeedExhaustedPage />} />
          {/* Task 11 — always-visible directed-notes inbox */}
          <Route path="notes" element={<NotesInboxPage />} />
          <Route path="batches" element={<BatchListPage />} />
          <Route path="batches/:id" element={<BatchDetailPage />} />
          <Route path="admin/export-formats" element={<ExportFormatListPage />} />
          <Route path="admin/export-formats/new" element={<ExportFormatWizardPage />} />
          <Route path="admin/export-formats/:id" element={<ExportFormatDetailPage />} />
          <Route path="admin/export-history" element={<ExportHistoryPage />} />
          <Route path="admin/outputs" element={<OutputsCatalogPage />} />
          <Route path="admin/connections" element={<ConnectionsPage />} />
          <Route path="admin/agents" element={<ConnectedAgentsPage />} />
          <Route path="admin/transform-plugins" element={<TransformPluginListPage />} />
          <Route path="admin/transform-plugins/:id" element={<TransformPluginDetailPage />} />
          <Route path="admin/named-chains" element={<NamedChainListPage />} />
          <Route path="admin/named-chains/new" element={<NamedChainCreateDialog />} />
          <Route path="admin/named-chains/:id" element={<NamedChainDetailPage />} />
          <Route path="admin/input-channels" element={<InputChannelsPage />} />
          <Route path="admin/analytics" element={<AnalyticsPage />} />
          {/* Phase 18 E3: analytics operator drill-in (route added AFTER the base
              analytics route to avoid pattern shadowing). The operator-trend
              API enforces analytics.view_own / analytics.view_others. */}
          <Route path="analytics/operators/:id" element={<OperatorDetailPage />} />
          {/* Phase 18 F3: document drill-through. Permission gate is rendered
              inline inside DocumentTrailPage (matches WU-18.D pattern). */}
          <Route path="analytics/documents/:id/trail" element={<DocumentTrailPage />} />
          {/* Phase 18 F4: batch drill-through. Route added after the
              document trail route for ordering consistency. */}
          <Route path="analytics/batches/:id/trail" element={<BatchTrailPage />} />
          <Route path="admin/reports" element={<ReportBuilderPage />} />
          <Route path="admin/llm" element={<LlmSettingsPage />} />
          <Route path="admin/llm/usage" element={<LlmUsagePage />} />
          <Route path="batch-fix/:batchId" element={<BatchFixPage />} />
          <Route path="settings" element={<OperatorPreferencesPage />} />
          <Route path="ops" element={<OpsConsolePage />} />
          {/* Phase 18 D8: /monitor now redirects to the new Ops Console. */}
          <Route path="monitor" element={<Navigate to="/ops" replace />} />
          <Route path="admin/settings" element={<TenantSettingsPage />} />
          <Route path="admin/failed-documents" element={<FailedDocumentsPage />} />
          <Route path="admin/ocr-health" element={<OcrHealthPage />} />
          {/* R-244 — Post-export retention queue */}
          <Route path="ops/retention" element={<RetentionQueuePage />} />
          {/* R-245 — Classification queue (parked docs awaiting type) */}
          <Route path="ops/classify" element={<ClassifyQueuePage />} />
          {/* R-274 7d — Pending Release: held manual export bundles */}
          <Route path="pending-release" element={<PendingReleasePage />} />
          {/* Qd-Qe — document history (awaiting-purge / purged + reprocess) */}
          <Route path="history" element={<HistoryPage />} />
          {/* F-065 — read-only automation guide (tenant-gated server-side). */}
          <Route path="rules-guide" element={<AutomationGuidePage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
